import type { IconName } from "@bundle/tokens";
import { RadioGroup } from "radix-ui";
import { Icon } from "./Icon.tsx";
import { cx } from "./tokens.ts";

export type SegmentedOption<K extends string = string> = {
  value: K;
  label: string;
  icon?: IconName;
  /** A count after the label (caption, tertiary): filter rows ("Guests 3"). */
  count?: number;
  disabled?: boolean;
};

export type SegmentedControlProps<K extends string = string> = {
  options: SegmentedOption<K>[];
  value: K;
  onChange: (value: K) => void;
  /** The group's name for assistive tech ("Picker", "Filter channels"). */
  accessibilityLabel: string;
  /**
   * "segmented" (default): one track with the chosen segment raised (iOS `Picker(.segmented)`, M3
   * `SingleChoiceSegmentedButtonRow`). "pills": separate outlined capsules, for filter rows (FilterChips natively).
   * "tabs": a page's tab strip (Slack's Drafts & sent): plain labels, the chosen one underlined.
   */
  variant?: "segmented" | "pills" | "tabs";
  /** Control height: sm = button.sm, md = button.md (default). */
  size?: "sm" | "md";
  /** Segments share the full width equally (default: each hugs its label). */
  fill?: boolean;
  /** The group; each option is `<testID>-<value>`. */
  testID: string;
};

/**
 * Choose exactly one of a few options. Radio-group semantics (Radix RadioGroup): Tab enters at the chosen option,
 * arrow keys move and choose; pressing the chosen option does nothing.
 */
export function SegmentedControl<K extends string>({ options, value, onChange, accessibilityLabel, variant = "segmented", size = "md", fill, testID }: SegmentedControlProps<K>) {
  return (
    <RadioGroup.Root
      className={cx("b-segmented", `b-segmented-${variant}`, `b-segmented-${size}`, fill === true && "b-segmented-fill")}
      value={value}
      onValueChange={(v) => {
        if (v !== value) onChange(v as K);
      }}
      orientation="horizontal"
      loop
      aria-label={accessibilityLabel}
      data-testid={testID}
    >
      {options.map((o) => (
        <RadioGroup.Item key={o.value} value={o.value} disabled={o.disabled} className="b-segment" data-testid={`${testID}-${o.value}`}>
          {o.icon === undefined ? null : <Icon name={o.icon} size="icon.sm" />}
          <span className="b-segment-label">{o.label}</span>
          {o.count === undefined ? null : <span className="b-segment-count">{o.count}</span>}
        </RadioGroup.Item>
      ))}
    </RadioGroup.Root>
  );
}
